import { I18n } from "react-redux-i18n";
import React, { useEffect } from "react";
import isNil from "lodash/isNil";

import {
  getModuleFolders,
  GetModuleFoldersParams,
} from "@common/actions/folders";
import { FolderType } from "@common/dto/folder/type";
import { FolderContext } from "@common/dto/folder/context";
import { getFolderFields } from "@common/actions/formats";
import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import { HttpErrorDto } from "@common/dto/http";
import { useMessage } from "@common/hooks/message";
import { generateId } from "@common/utils/common";
import {
  SkipLogicCondition,
} from "@common/components/skip-logic/skip-logic.types";
import { TreeGetIdArgs } from "@common/components/Tree";
import {
  FormatDetails,
  FormatInfo,
  FormatInfoWrapper,
  runFormatPayload,
  SaveFormatPayload,
  SpreadsheetType,
  SmartColumn,
} from "@modules/spreadsheets/dto/spreadsheet/type";
import { saveFormat } from "@modules/spreadsheets/actions/spreadsheet";
import { computeQueryModel, hasConditionValue, isValueOptionalOperator, MISSING_VALUE_ERROR_MESSAGE, validateQueryConditions } from "../spreadsheet-result/components/table/query.helpers";
import { convertFilterValueToDisplay, convertFilterValue } from "../spreadsheet-result/components/table/filter-utils";
import { loadIndividualSpreadsheetFormat, loadPedigreeSpreadsheetFormat } from "@common/actions/table";

import {
  InternalFieldPrimaryList,
  SpreadsheetManagementDraggedField,
  SpreadsheetManagementProps,
} from "./spreadsheet-management.types";

import { useColumnsTableController } from "./columns-table.controller";
import { useSortTableController } from "./sort-table.controller";
import { useFormatManagementController } from "../../controllers/format-management";
import { groupTableFieldsByParent } from "./columns-grouping.utils";
import {
  OPERATOR_MAPPING,
  SPREADSHEET_DEFAULTS,
  SPREADSHEET_FIELD_CONTEXT,
  SPREADSHEET_TYPE,
} from "@modules/spreadsheets/const/common";
import { SPREADSHEET_TABLE_CONSTANTS } from "../spreadsheet-result/components/table/table.types";
import { FieldOption } from "@common/dto/field/option";
import { normalizeLookupValues, hydrateQueryModelFromSmartColumns, cleanSmartColumnsForApi, cleanQueryModelForApi } from "../spreadsheet-result/components/table/data-converter";
import {  ensureRowCountColumn, filterOutRowCountColumns } from "../spreadsheet-result/components/table/utils/column.utils";

export const useSpreadsheetManagementController = (
  props: SpreadsheetManagementProps
) => {
  const itemUniqProperty = "internalId" as const;
  const draggedUniqProperty = "draggedId" as const;

  const { spreadsheetType, propsSpreadsheetId, initialFolderId, initialFamilyId } = props;

  const { showErrorMessage, messageProps, closeMessage, showConfirmMessage,showInfoMessage } =
    useMessage();
  const [spreadsheetId, setSpreadsheetId] = React.useState<number>(propsSpreadsheetId);
  const [individualFolders, setIndividualFolders] = React.useState<
    FolderPrimaryListDto[]
  >([]);
  const [pedigreeFolders, setPedigreeFolders] = React.useState<
    FolderPrimaryListDto[]
  >([]);
  const [selectedFolder, setSelectedFolder] = React.useState<
    FolderPrimaryListDto | undefined
  >();
  const [showResults, setShowResults] = React.useState(false);
  const [runPayload, setRunPayload] = React.useState<runFormatPayload | null>(
    null
  );

  const [fields, setFields] = React.useState<InternalFieldPrimaryList[]>([]);
  const [isFieldsFromSearch, setIsFieldsFromSearch] = React.useState<boolean>(false);
  const [selectedFields, setSelectedFields] = React.useState<
    InternalFieldPrimaryList[]
  >([]);
  const [queryBuilderConditions, setQueryBuilderConditions] = React.useState<
    SkipLogicCondition[]
  >([]);

  const [isPedigreeFoldersLoading, setIsPedigreeFoldersLoading] =
    React.useState(false);
  const [isIndividualFoldersLoading, setIsIndividualFoldersLoading] =
    React.useState(false);
  const [isFieldsLoading, setIsFieldsLoading] = React.useState(false);
  const [isSaveFormatLoading, setIsSaveFormatLoading] = React.useState(false);
  const [isRunSpreadsheetLoading, setIsRunSpreadsheetLoading] =
    React.useState(false);
  const [currentFormat, setCurrentFormat] = React.useState<
    | {
        id: number;
        name: string;
        description?: string|undefined;
        folderId: number;
      }
   
  >(
    {
      id: 0,  
      name: "",
      description: "",
      folderId: 0,
    }
  );
  const systemFieldsLoadedRef = React.useRef(false);
  const foldersLoadedRef = React.useRef(false);
  const isInitializingRef = React.useRef(true);
  const initialFolderAppliedRef = React.useRef(false);


  const {
    setSortTableFields,
    handleSortTableFieldsRemove,
    columnsSortTable,
    sortTableFields,
  } = useSortTableController({ showConfirmMessage });

  const handleFieldAddQueryClick = React.useCallback(
    (row: SpreadsheetManagementDraggedField) => {
      const data: SkipLogicCondition = {
        id: generateId().toString(),
        field: row.field,
        leftParenthesis: false,
        rightParenthesis: false,
        parentField: row.parentField as any,
      };

      setQueryBuilderConditions((prevConditions) => [...prevConditions, data]);
    },
    []
  );

  const {
    setColumnsTableFields,
    handleColumnsTableFieldDrop,
    handleColumnsTableFieldsRemove,
    columnsTable,
    columnsTableFields,
  } = useColumnsTableController({
    selectedFolder,
    selectedFields,
    showConfirmMessage,
    onFieldSortClick: (field) => {
      if (field && !sortTableFields.map(({ id }) => id).includes(field.id)) {
        setSortTableFields([...sortTableFields, field]);
      }
    },
    onFieldRemoveClick: (fieldId) => {
      setSortTableFields((fields) =>
        fields.filter((field) => field.id !== fieldId)
      );
    },
    onFieldsRemoveClick: () => {
      setSortTableFields([]);
    },
    onFieldAddQueryClick: handleFieldAddQueryClick,
  });

  const formatManagementController = useFormatManagementController({
    spreadsheetType,
    queryBuilderConditions,
    showErrorMessage,
    currentFormat,
  });

  const handleLoadFormat = React.useCallback(async (format: FormatInfo) => {
    try {
      
      if (!format?.id) {
        showErrorMessage(I18n.t("Invalid format selected"));
        return;
      }

      // Load format data based on spreadsheet type
      const loadFormatApi = spreadsheetType === SpreadsheetType.individual 
        ? loadIndividualSpreadsheetFormat 
        : loadPedigreeSpreadsheetFormat;

      const formatData = await loadFormatApi(format.id);
      
      if (!formatData) {
        showErrorMessage(I18n.t("Failed to load format data"));
        return;
      }

      const columnModel = formatData.columnModel || formatData;
      const queryModel = formatData.queryModel;
      
      
      if (!columnModel?.smartColumns) {
        showErrorMessage(I18n.t("Invalid format structure - no columns found"));
        return;
      }

      const filteredSmartColumns = (columnModel.smartColumns || []).filter(
        (smartCol: SmartColumn) =>
          smartCol?.field?.fieldid && smartCol.field.fieldid > 0
      );

      // Enrich queryModel with dropdown/options/lookup values from smartColumns before storing
      const enrichedQueryModelForStorage = hydrateQueryModelFromSmartColumns(queryModel, filteredSmartColumns);

      if (typeof window !== "undefined" && (window as any).__SPREADSHEET_STORE__) {
        try {
          const store = (window as any).__SPREADSHEET_STORE__;
          const { storeFormatData, clearFormatData } = require("@modules/spreadsheets/actions/spreadsheetFormat");
          const spreadsheetTypeKey =
            spreadsheetType === SpreadsheetType.individual ? "Individual" : "Pedigree";
          const columnModelForStorage = {
            ...(columnModel || {}),
            smartColumns: filteredSmartColumns.map((smartCol: SmartColumn) =>
              JSON.parse(JSON.stringify(smartCol))
            ),
          };
          // Use enriched queryModel for storage
          const queryModelForStorage = enrichedQueryModelForStorage
            ? JSON.parse(JSON.stringify(enrichedQueryModelForStorage))
            : { whereClauses: [] };
          const formatDataToStore: any = {
            format: {
              id: Number(format.id),
              name: format.name || "",
              description: format.description || "",
              folderId: format.folderId || 0,
            },
            columnModel: columnModelForStorage,
            queryModel: queryModelForStorage,
            timestamp: Date.now(),
          };
          if ((formatData as any)?.filter) {
            formatDataToStore.filter = JSON.parse(JSON.stringify((formatData as any).filter));
          }

          // If we're in a new spreadsheet flow (ID 0), clear it from Redux
          // since we're now loading an existing format
          if (spreadsheetId === 0) {
            store.dispatch(clearFormatData(0, spreadsheetTypeKey));
          }

          // Store the loaded format data with the loaded format ID only
          store.dispatch(storeFormatData(Number(format.id), spreadsheetTypeKey, formatDataToStore));
        } catch (e) {
          console.warn("Could not store loaded format in Redux:", e);
        }
      }

      // Populate all components with loaded format data
      
      // Update spreadsheetId to the loaded format ID (removes us from "new spreadsheet" mode)
      setSpreadsheetId(format.id);
      
      // Update current format when a format is loaded
      setCurrentFormat({
        id: format.id,
        name: format.name,
        description: format.description,
        folderId: format.folderId,
      });

      // Populate columns table with loaded format columns (exclude dummy/system unknown fields)
      const loadedColumns = filteredSmartColumns
        .map((smartCol: SmartColumn) => ({
        id: generateId().toString(),
        field: {
          id: smartCol.field.fieldid,
          name: smartCol.field.fieldname,
          system: smartCol.field.system,
          type: smartCol.field.type,
          context: spreadsheetType === SpreadsheetType.individual ? 1 : 2,
          imageURL: smartCol.parentField?.imageURL || smartCol.field.imageURL,
          fullPath: smartCol.field.fieldnameWithPath || smartCol.field.fieldname,
          internalId: generateId(),
        },
        folder: selectedFolder || {
          id: 0,
          name: "System",
          parentid: 0,
          secToken: 0,
          system: true,
          context: spreadsheetType === SpreadsheetType.individual ? 1 : 2,
          imageURL: "",
          imageURLSelected: "",
        },
        editable: smartCol.editable ?? false,
        parentField: smartCol.parentField || undefined,
      }));

      setColumnsTableFields(groupTableFieldsByParent(loadedColumns));

      // Populate sort table with loaded format sorting
      // Sort by sortOrder to preserve the order from the saved format
      const sortFields = filteredSmartColumns
        .filter((smartCol: SmartColumn) => smartCol.sortOrder && smartCol.sortOrder > 0)
        .sort((a: SmartColumn, b: SmartColumn) => (a.sortOrder || 0) - (b.sortOrder || 0))
        .map((smartCol: SmartColumn) => ({
          id: generateId().toString(),
          field: {
            id: smartCol.field.fieldid,
            name: smartCol.field.fieldname,
            system: smartCol.field.system,
            type: smartCol.field.type,
            fullPath: smartCol.field.fieldnameWithPath || smartCol.field.fieldname,
            internalId: generateId(),
            imageURL: smartCol.parentField?.imageURL || smartCol.field.imageURL,
          },
          folder: selectedFolder || {
            id: 0,
            name: "System",
            parentid: 0,
            secToken: 0,
            system: true,
            context: spreadsheetType === SpreadsheetType.individual ? 1 : 2,
            imageURL: "",
            imageURLSelected: "",
          },
          direction: smartCol.sortAsc ? "ascending" as any : "descending" as any,
        }));

      setSortTableFields(sortFields);

      // Use the enriched queryModel we created earlier for populating query builder
      // (enrichedQueryModelForStorage was created at line 207 with filteredSmartColumns)

      // Populate query builder with loaded format conditions
      if (enrichedQueryModelForStorage?.whereClauses) {
        const queryConditions = enrichedQueryModelForStorage.whereClauses.map((clause: { field: { fieldid: number;imageURL: string; fieldname: string; system: boolean; type: number,dropdownValues?: string[],options?: FieldOption[] }; operator: number; value: any; leftParenthesis?: boolean; rightParenthesis?: boolean; andOr?: string; parentField?: { fieldid?: number; fieldId?: number; fieldname?: string; fieldName?: string; system?: boolean; type?: number; imageURL?: string;} }) => {
          // Extract string value from clause.value object if it's an object
          let rawValue = "";
          if (clause.value) {
            if (typeof clause.value === "string") {
              rawValue = clause.value;
            } else if (clause.value.dataValue !== undefined) {
              rawValue = clause.value.dataValue;
            } else if (clause.value.rawValue !== undefined) {
              rawValue = clause.value.rawValue;
            } else if (clause.value.updateValue !== undefined) {
              rawValue = clause.value.updateValue;
            } else {
              // Fallback: convert to string if it's still an object
              rawValue = String(clause.value);
            }
          }

          // Convert raw value to display value using field information
          const fieldInfo = {
            fieldId: clause.field.fieldid,
            fieldName: clause.field.fieldname,
            isSystem: clause.field.system
          };
          const stringValue = convertFilterValueToDisplay(fieldInfo, rawValue);

          const condition: any = {
            id: generateId().toString(),
            field: {
              id: clause.field.fieldid,
              name: clause.field.fieldname,
              system: clause.field.system,
              type: clause.field.type,
              context: spreadsheetType === SpreadsheetType.individual ? 1 : 2,
              imageURL: clause.parentField?.imageURL || clause.field.imageURL,
              internalId: generateId(),
              fullPath: clause.field.fieldname,
              options: clause.field.options|| clause.field.dropdownValues?.map((value: string) => ({
                actualValue: value,
                displayValue: value,
              })),
            },
            operator: getOperatorFromValue(clause.operator) as any,
            value: stringValue,
            leftParenthesis: clause.leftParenthesis || false,
            rightParenthesis: clause.rightParenthesis || false,
            andOR: clause.andOr as any,
          };

          // Add parentField if it exists in the original clause
          if (clause.parentField) {
            //use matching smartColumn to get correct parentField
            const matchingSmartCol = filteredSmartColumns.find(
                (col: SmartColumn) => col.field.fieldid === clause.field.fieldid
            );

            const parentFieldFromCol = matchingSmartCol?.parentField;
            const pf: any = parentFieldFromCol || clause.parentField;
            const parentFieldId = parentFieldFromCol?.fieldid ||
                clause.parentField.fieldid ||
                clause.parentField.fieldId;

            if (parentFieldId && parentFieldId > 0) {
              condition.parentField = {
                id: parentFieldId,
                name: pf.fieldname || pf.fieldName || pf.name || '',
                system: pf.system || false,
                type: pf.fieldtype?.typeid || pf.type || 5,
                context: spreadsheetType === SpreadsheetType.individual ? 1 : 2,
                imageURL: pf.imageURL || '',
                internalId: generateId(),
                fullPath: pf.fieldnameWithPath || pf.fieldname || pf.fieldName || pf.name || '',
              };
            }
          }

          return condition;
        });

        setQueryBuilderConditions(queryConditions);
      }

      // Close the format management dialog
      formatManagementController.closeLoadFormat();
      
      // User stays in builder to see loaded format
      
      showInfoMessage(I18n.t("Format loaded successfully"));
      
    } catch (error) {
      const errorMessage = (error as HttpErrorDto)?.entity || 
                          (error as Error)?.message || 
                          I18n.t("Failed to load format");
      showErrorMessage(errorMessage);
    }
  }, [spreadsheetType, selectedFolder, setColumnsTableFields, setSortTableFields, setQueryBuilderConditions, formatManagementController, showErrorMessage, showInfoMessage]);

  const saveFormatInternal = React.useCallback(
    async (formatDetails: FormatDetails, formatInfo?: FormatInfoWrapper) => {
      try {
        setIsSaveFormatLoading(true);
        const smartColumns = columnsTableFields.map((columnField, index) => {
          const sortFieldIndex = sortTableFields.findIndex(
            (sf) => sf.field.id === columnField.field.id
          );
          const sortField = sortTableFields[sortFieldIndex];
          const hasSortField = sortFieldIndex !== -1;
          const sortOrder = hasSortField ? sortFieldIndex + 1 : 0;
          const sortAsc =
            hasSortField && sortField?.direction
              ? sortField.direction === "ascending"
              : false;

          const column: any = {
            width: SPREADSHEET_DEFAULTS.WIDTH,
            sortOrder,
            sortAsc,
            columnOrder: index + 1, // Display order
            field: {
              fieldid: columnField.field.id,
              fieldname: columnField.field.name,
              system: columnField.field.system,
              fieldcontext:
                spreadsheetType === SpreadsheetType.individual
                  ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL
                  : SPREADSHEET_FIELD_CONTEXT.PEDIGREE,
              type: columnField.field.type,
            },
          };

          // Conditionally add parentField to smartColumns if it exists
          if (columnField.parentField) {
            const pf: any = columnField.parentField as any;
            column.parentField = {
              fieldid: pf.fieldid ?? pf.id,
              fieldname: pf.fieldname ?? pf.fieldName ?? pf.name,
              system: pf.system ?? false,
              fieldcontext:
                spreadsheetType === SpreadsheetType.individual
                  ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL
                  : SPREADSHEET_FIELD_CONTEXT.PEDIGREE,
              type: columnField.parentField.system
                ? SPREADSHEET_TABLE_CONSTANTS.FIELD_TYPES.SYSTEM
                : columnField.parentField.type,
            };
          }

          return column;
        });

        // Transform whereClauses to match table.controller.tsx format
        const whereClauses = queryBuilderConditions.map((condition, index) => {
          // Convert display value to actual value for API
          const fieldInfo = {
            fieldId: condition.field.id,
            fieldName: condition.field.name,
            isSystem: condition.field.system
          };
          const actualValue = convertFilterValue(fieldInfo, condition.value || "");

          const clause: any = {
            field: {
              fieldid: condition.field.id,
              type: condition.field.system === true ? SPREADSHEET_TABLE_CONSTANTS.FIELD_TYPES.SYSTEM : condition.field.type,
              fieldcontext:
                spreadsheetType === SpreadsheetType.individual
                  ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL
                  : SPREADSHEET_FIELD_CONTEXT.PEDIGREE,
              system: condition.field.system,
            },
            operator: getOperatorValue(condition.operator || "EQUALS"),
            value: actualValue,
          ...(condition.andOR && { andOr: condition.andOR }), // Note: andOr not andOR
            leftParenthesis: condition.leftParenthesis,
            rightParenthesis: condition.rightParenthesis,
          };

        

          // Conditionally add parentField to whereClauses if it exists
          if (condition.parentField) {
            const pf: any = condition.parentField as any;
            clause.parentField = {
              fieldid: condition.parentField.id,
              type:
                condition.parentField.system === true
                  ? SPREADSHEET_TABLE_CONSTANTS.FIELD_TYPES.SYSTEM
                  : condition.parentField.type,
              fieldcontext:
                spreadsheetType === SpreadsheetType.individual
                  ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL
                  : SPREADSHEET_FIELD_CONTEXT.PEDIGREE,
              system: pf.system ?? false,
            };
          }

          return clause;
        });

        const unifiedQuery = computeQueryModel(spreadsheetType, {
          userFilter: { whereClauses },
        });

        const payload: SaveFormatPayload = {
          context:
            spreadsheetType === SpreadsheetType.individual
              ? SPREADSHEET_TYPE.individual
              : SPREADSHEET_TYPE.pedigree,
          ssFormat: {
            id: formatInfo?.format?.id || 0,
            name: formatDetails.name,
            folderId: formatDetails.folderId,
            description: formatDetails.description || "",
          },
          columnModel: {
            smartColumns: cleanSmartColumnsForApi(smartColumns),
          },
          queryModel: cleanQueryModelForApi(computeQueryModel(spreadsheetType, {
            userFilter: { whereClauses },
            normalizeForSave: true,
          })),
        };

        let result = await saveFormat(payload);
        if (typeof result === "number") {
          setSpreadsheetId(result);
        }
        formatManagementController.closeSaveFormat();
      } catch (err) {
        const errorMessage =
          (err as HttpErrorDto)?.entity || I18n.t("Failed to save formats");
        showErrorMessage(errorMessage);
      } finally {
        setIsSaveFormatLoading(false);
      }
    },
    [
      formatManagementController,
      showErrorMessage,
      columnsTableFields,
      sortTableFields,
      queryBuilderConditions,
      spreadsheetType,
    ]
  );

  const handleSaveFormat = React.useCallback(
    async (formatDetails: FormatDetails, formatInfo?: FormatInfoWrapper) => {
      try {
        const isNewFormat = !formatInfo?.format?.id;

        if (!isNewFormat) {
          showConfirmMessage({
            cancelText: I18n.t("No"),
            confirmText: I18n.t("Yes"),
            confirmBtnId: "btn-message-box-ok",
            cancelBtnId: "btn-message-box-dont-cancel",
            content: I18n.t(
              "This name has already been used, would you like to overwrite the current format being used by this name?"
            ),
            onCustomOk: async () => {
              // Proceed with saving the format
              await saveFormatInternal(formatDetails, formatInfo);
            },
          });
          return;
        }

        await formatManagementController.getAndSetFormats(
          formatDetails.folderId
        );

        // Check if format name exists after loading the formats
        if (
          formatManagementController.checkFormatNameExists(
            formatDetails.name,
            formatDetails.folderId
          )
        ) {
          const existingFormat = formatManagementController.formats.find(
            (format) =>
              format.name.trim().toLowerCase() ===
                formatDetails.name.trim().toLowerCase() &&
              format.folderId === formatDetails.folderId
          );

          showConfirmMessage({
            cancelText: I18n.t("No"),
            confirmText: I18n.t("Yes"),
            confirmBtnId: "btn-message-box-ok",
            cancelBtnId: "btn-message-box-dont-cancel",
            content: I18n.t(
              "This name has already been used, would you like to overwrite the current format being used by this name?"
            ),
            onCustomOk: async () => {
              // Create format info with the existing format ID for overwriting
              const overwriteFormatInfo = existingFormat
                ? { format: existingFormat }
                : formatInfo;
              await saveFormatInternal(formatDetails, overwriteFormatInfo);
            },
          });
          return;
        }

        await saveFormatInternal(formatDetails, formatInfo);
      } catch (err) {
        const errorMessage =
          (err as HttpErrorDto)?.entity || I18n.t("Failed to save formats");
        showErrorMessage(errorMessage);
      }
    },
    [
      formatManagementController,
      showConfirmMessage,
      showErrorMessage,
      saveFormatInternal,
    ]
  );

  const getOperatorValue = (operator: string): number => {
    const operatorMap: Record<string, number> = OPERATOR_MAPPING;
    return operatorMap[operator] || 1; // Default to EQUALS if not found
  };

  const getOperatorFromValue = (value: number): string => {
    const operatorMap: Record<string, number> = OPERATOR_MAPPING;
    const operator = Object.keys(operatorMap).find(key => operatorMap[key] === value);
    return operator || "EQUALS"; // Default to EQUALS if not found
  };

  const isAllowedSaveFormatSubmit = React.useCallback(
    ({
      newFormatName,
      folderId,
    }: {
      newFormatName?: string;
      folderId?: number;
    }) =>
      Boolean(newFormatName?.trim().length) &&
      folderId?.toString() !== "" &&
      !isNil(folderId) &&
      !isSaveFormatLoading,
    [isSaveFormatLoading]
  );

  const spreadsheetTypeConfig: Record<
    SpreadsheetType,
    { loadFmtTitle: string; saveFmtTitle: string; idPrefix: string }
  > = {
    [SpreadsheetType.pedigree]: {
      loadFmtTitle: I18n.t("Load Pedigree Spreadsheet Format"),
      saveFmtTitle: I18n.t("Save Pedigree Spreadsheet Format"),
      idPrefix: "PSSC",
    },
    [SpreadsheetType.individual]: {
      loadFmtTitle: I18n.t("Load Individual Spreadsheet Format"),
      saveFmtTitle: I18n.t("Save Individual Spreadsheet Format"),
      idPrefix: "ISSC",
    },
  };

  const currentSpreadsheetTypeConfig = spreadsheetTypeConfig[spreadsheetType];

  const handleGetPedigreeFolders = React.useCallback(
    async (params?: GetModuleFoldersParams) => {
      try {
        setIsPedigreeFoldersLoading(true);
        const folders = await getModuleFolders({
          folderTypeId: FolderType.pedigree,
          systemFolderConfig: {
            include: spreadsheetType === SpreadsheetType.pedigree,
          },
          setUp: {
            rootFolderName: I18n.t("Pedigree Data Folders"),
            rootFolderImgUrl: "gif/df_pedigree.gif",
            rootFolderId: "pedigree-folder",
          },
          ...params,
        });

        setPedigreeFolders(folders);
      } catch (err) {

        const errorMessage =
          (err as HttpErrorDto)?.entity ||
          I18n.t("Failed to get pedigree folders");
        showErrorMessage(errorMessage);
      } finally {
        setIsPedigreeFoldersLoading(false);
      }
    },
    [spreadsheetType, showErrorMessage]
  );

  const handleRowDrop = React.useCallback(
    (dragIndex: number, hoverIndex: number) => {
      setColumnsTableFields((prevFields) => {
        const draggedField = prevFields[dragIndex];
        const targetField = prevFields[hoverIndex];
        
        if (!draggedField || !targetField) return prevFields;

        const getParentKey = (field: SpreadsheetManagementDraggedField) => {
          const parent = field.parentField as any;
          if (!parent) return null;
          return (
            parent.id ??
            parent.fieldid ??
            parent.fieldId ??
            parent.name ??
            parent.fieldname ??
            parent.fieldName ??
            null
          );
        };

        const dragParentKey = getParentKey(draggedField);
        const targetParentKey = getParentKey(targetField);
        const dragHasParent = dragParentKey !== null;
        const targetHasParent = targetParentKey !== null;

        if (dragHasParent !== targetHasParent) {
          return prevFields;
        }

        if (dragHasParent && targetHasParent && dragParentKey !== targetParentKey) {
          return prevFields;
        }
        
        const newFields = [...prevFields];
        newFields.splice(dragIndex, 1);
        newFields.splice(hoverIndex, 0, draggedField);
        return newFields;
      });
    },
    [setColumnsTableFields]
  );

  const handleRunSpreadsheet = React.useCallback(async () => {
    try {
      setIsRunSpreadsheetLoading(true);

    // Check if any columns are selected
    if (!columnsTableFields || columnsTableFields.length === 0) {
      showErrorMessage(I18n.t("Please select fields to run the spreadsheet"));
      setIsRunSpreadsheetLoading(false);
      return;
    }    
    // Validate query conditions (AND/OR and parentheses)
    // Similar to GWT validation in InvitesQueryWnd.validate()
    // Always validate if there are any conditions
    if (queryBuilderConditions && queryBuilderConditions.length > 0) {
      const validation = validateQueryConditions(queryBuilderConditions);
      if (!validation.isValid) {
        const errorMsg =
          validation.errorMessage ||
          I18n.t(
            "The query is not formed properly, please verify that the AND/OR column is entered correctly for each row."
          );
        showErrorMessage(errorMsg);
        setIsRunSpreadsheetLoading(false);
        return;
      }

      const fieldsWithMissingValues: string[] = [];

      queryBuilderConditions.forEach((condition) => {
        if (!condition || isValueOptionalOperator(condition.operator)) {
          return;
        }
        if (!hasConditionValue(condition.value)) {
          const fieldName =
            condition.field?.name ||
            (condition.field as any)?.fieldname ||
            (condition.field as any)?.fieldnameWithPath ||
            I18n.t("Unknown Field");
          fieldsWithMissingValues.push(fieldName);
        }
      });

      if (fieldsWithMissingValues.length > 0) {
        showErrorMessage(
          `${MISSING_VALUE_ERROR_MESSAGE} ${fieldsWithMissingValues.join(", ")}`
        );
        setIsRunSpreadsheetLoading(false);
        return;
      }
    }
      setSpreadsheetId(currentFormat.id);
    let currentModulo = 0;
    const parentFieldIdToModulo = new Map<number, number>();

    // Get preserved column widths from Redux (for both saved and new spreadsheets)
    // Also check previous runPayload as fallback for new spreadsheets
    const previousWidthsMap = new Map<number, number>();
    
    // First, try to get widths from Redux
    if (typeof window !== 'undefined' && (window as any).__SPREADSHEET_STORE__) {
      try {
        const store = (window as any).__SPREADSHEET_STORE__;
        const state = store.getState();
        const spreadsheetTypeKey = spreadsheetType === SpreadsheetType.individual ? 'Individual' : 'Pedigree';
        const spreadsheetId = currentFormat.id || 0; // Use 0 for new spreadsheets
        const typeData = state?.spreadsheetFormat?.[spreadsheetTypeKey] || {};
        const storedData = typeData[spreadsheetId];
        
        if (storedData?.columnModel?.smartColumns) {
          storedData.columnModel.smartColumns.forEach((col: any) => {
            if (col.field?.fieldid && col.width) {
              previousWidthsMap.set(col.field.fieldid, col.width);
            }
          });
        }
      } catch (e) {
        console.warn('Could not retrieve column widths from Redux:', e);
      }
    }
 

    let smartColumns = columnsTableFields.map((columnField:any, index) => {
      const sortFieldIndex = sortTableFields.findIndex(
        (sf) => sf.field.id === columnField.field.id
      );
      const sortField = sortTableFields[sortFieldIndex];
      const hasSortField = sortFieldIndex !== -1;
      const sortOrder = hasSortField ? sortFieldIndex + 1 : 0;
      const sortAsc =
        hasSortField && sortField?.direction
          ? sortField.direction === "ascending"
          : false;
      const isSystem = columnField.field.system || false;
      const fieldType = columnField.field.type || columnField.field.fieldtype?.typeid;

      // Get width from Redux if available, otherwise use columnField.width or default
      const fieldId = columnField.field.id;
      const preservedWidth = previousWidthsMap.get(fieldId) || columnField.width || SPREADSHEET_DEFAULTS.WIDTH;

      // Base column object with proper typing
      const column: any = {
        field: {
          fieldid: columnField.field.id,
          fieldname: columnField.field.name,
          system: isSystem,
          fieldnameWithPath: columnField.field.fullPath,
          fieldcontext: columnField.field.context === SPREADSHEET_TYPE.individual
            ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL
            : SPREADSHEET_FIELD_CONTEXT.PEDIGREE,
          type:  fieldType,
          dropdownValues: columnField.field.options??columnField.field.dropdownValues,
          lookupValues: normalizeLookupValues(columnField.lookupValues),
          imageURL: columnField.field.imageURL,
        },
        ...(columnField.parentField && { parentField: columnField.parentField }),
        modulo: columnField.modulo,
        columnOrder: index + 1,
        sortOrder,
        sortAsc,
        editable: columnField.editable,
        securityEditable: true,
        width: preservedWidth,
        visible: true,
      };
      // Conditionally add parentField only if it exists
      if (columnField.parentField) {
        column.parentField = {
          fieldid: columnField.parentField.id || columnField.parentField.fieldid,
          fieldname: columnField.parentField.name || columnField.parentField.fieldname,
          system: columnField.parentField.system,
          fieldcontext:
            spreadsheetType === SpreadsheetType.individual
              ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL
              : SPREADSHEET_FIELD_CONTEXT.PEDIGREE,
          type: columnField.parentField.system
            ? SPREADSHEET_TABLE_CONSTANTS.FIELD_TYPES.SYSTEM
            : (columnField.parentField.type || columnField.parentField.fieldtype?.typeid),
        };

        // Assign modulo per distinct parentField id, toggling between 0 and 1
        const parentId = (columnField.parentField.id || columnField.parentField.fieldid) as number | undefined;
        if (parentId && typeof parentId === 'number') {
          if (parentFieldIdToModulo.has(parentId)) {
            column.modulo = parentFieldIdToModulo.get(parentId);
          } else {
            column.modulo = currentModulo;
            parentFieldIdToModulo.set(parentId, currentModulo);
            currentModulo = currentModulo === 0 ? 1 : 0;
          }
        }
      }

      return column;
    });
      
      smartColumns = ensureRowCountColumn(smartColumns, spreadsheetType);

    // Transform whereClauses to match table.controller.tsx format
    const whereClauses = queryBuilderConditions.map((condition, index) => {
      // Extract string value from condition.value if it's an object
      let rawValue = "";
      if (condition.value) {
        const conditionValue = condition.value as any;
        if (typeof conditionValue === "string") {
          rawValue = conditionValue;
        } else if (conditionValue.dataValue !== undefined) {
          rawValue = conditionValue.dataValue;
        } else if (conditionValue.rawValue !== undefined) {
          rawValue = conditionValue.rawValue;
        } else if (conditionValue.updateValue !== undefined) {
          rawValue = conditionValue.updateValue;
        } else {
          // Fallback: convert to string if it's still an object
          rawValue = String(conditionValue);
        }
      }

      // Convert display value to actual value for API
      const fieldInfo = {
        fieldId: condition.field.id,
        fieldName: condition.field.name,
        isSystem: condition.field.system
      };
      const actualValue = convertFilterValue(fieldInfo, rawValue);

      const clause: any = {
        field: {
          fieldid: condition.field.id,
          type: condition.field.type || condition.field.fieldtype?.typeid,
          fieldname: condition.field.name,
          fieldcontext:
            spreadsheetType === SpreadsheetType.individual
              ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL
              : SPREADSHEET_FIELD_CONTEXT.PEDIGREE,
          system: condition.field.system,
          imageURL: condition.field.imageURL,
          options: condition.field.options || condition.field.dropdownValues?.map((value: string) => ({
            actualValue: value,
            displayValue: value,
          })),
        },
        // fieldClass: "java.lang.String", // Default field class
        operator: getOperatorValue(condition.operator || "EQUALS"),
        value: actualValue,
        leftParenthesis: condition.leftParenthesis,
        rightParenthesis: condition.rightParenthesis,
      };

      // Include andOr per clause: pass provided value; else default AND on first clause only
      if (condition.andOR !== undefined) {
        clause.andOr = condition.andOR;
      }

      // Conditionally add parentField to whereClauses if it exists
      if (condition.parentField) {
        clause.parentField = {
          fieldid: (condition.parentField as any).fieldid ?? (condition.parentField as any).id,
          type:
            (condition.parentField as any).fieldtype?.typeid|| (condition.parentField as any).type,
          fieldcontext:
            spreadsheetType === SpreadsheetType.individual
              ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL
              : SPREADSHEET_FIELD_CONTEXT.PEDIGREE,
          system: (condition.parentField as any).system ?? false,
        };
      }

      return clause;
    });

    // Derive a top-level andOR from the first clause that specifies it
    const selectedAndOr = (whereClauses.find((c: any) => (c as any)?.andOr)?.andOr) as string | undefined;
    // Default to OR when multiple distinct fields exist in builder without explicit operator
    const multiplePairs = whereClauses.length >= 2;
    const andORNumeric = selectedAndOr === "OR" ? 1 : selectedAndOr === "AND" ? 0 : (multiplePairs ? 1 : undefined);


    // Build a unified query for run payload (no apiQuery here, just builder clauses)
    const unifiedRunQuery = computeQueryModel(spreadsheetType, {
      runPayloadQuery: { whereClauses, ...(andORNumeric !== undefined && { andOR: andORNumeric }) },
      normalizeForSave: true,
    });
    
    // Retrieve filter from Redux if it exists for this spreadsheet
    let filterFromRedux: { whereClauses: any[]; andOR?: number } | undefined = undefined;
    if (typeof window !== 'undefined' && (window as any).__SPREADSHEET_STORE__) {
      try {
        const store = (window as any).__SPREADSHEET_STORE__;
        const state = store.getState();
        const spreadsheetTypeKey = spreadsheetType === SpreadsheetType.individual ? 'Individual' : 'Pedigree';
        const typeData = state?.spreadsheetFormat?.[spreadsheetTypeKey] || {};
        const spreadsheetId = currentFormat.id;
        const storedData = typeData[spreadsheetId];
        if (storedData && storedData.filter && storedData.filter.whereClauses && storedData.filter.whereClauses.length > 0) {
          filterFromRedux = storedData.filter;
        }
      } catch (e) {
        console.warn('Could not retrieve filter from Redux:', e);
      }
    }

    const payload: runFormatPayload = {
      context: spreadsheetType === SpreadsheetType.individual ? 1 : 2,
      folderId: initialFolderId ?? 0,
      familyId: spreadsheetType === SpreadsheetType.individual ? (initialFamilyId ?? 0) : 0,
      ssFormat: {
        id: currentFormat.id,
        name: currentFormat.name,
        folderId: initialFolderId ?? 0,
        description: currentFormat.description,
      },
      columnModel: {
        smartColumns,
      },
      queryModel: unifiedRunQuery,
      // Include filter in run payload if it exists in Redux
      ...(filterFromRedux && { filter: filterFromRedux }),
    };
    setRunPayload(payload);

    
    // Initialize unsavedSSCount for new spreadsheets (when no saved spreadsheet ID)
    // Don't initialize to 0 for new spreadsheets - let the API response determine the count
    // Only switch to results mode if we successfully created the payload
    setShowResults(true);
    setIsRunSpreadsheetLoading(false);
  } catch (err) {
    const errorMessage =
      (err as HttpErrorDto)?.entity || I18n.t("Failed to run spreadsheet");
    showErrorMessage(errorMessage);
    setIsRunSpreadsheetLoading(false);
    // Don't switch to results mode on error
  }
}, [
  columnsTableFields,
  sortTableFields,
  queryBuilderConditions,
  spreadsheetType,
  selectedFolder,
  showErrorMessage,
  ensureRowCountColumn,
  initialFolderId,
  initialFamilyId,
]);

  const handleGetIndividualFolders = React.useCallback(
    async (params?: GetModuleFoldersParams) => {
      try {
        setIsIndividualFoldersLoading(true);
        const folders = await getModuleFolders({
          folderTypeId: FolderType.individual,
          systemFolderConfig: {
            include: true,
          },
          setUp: {
            rootFolderName: I18n.t("Individual Data Folders"),
            rootFolderImgUrl: "gif/df_individual.gif",
            rootFolderId: "individual-folder",
          },
          ...params,
        });
        setIndividualFolders(folders);
      } catch (err) {

        const errorMessage =
          (err as HttpErrorDto)?.entity ||
          I18n.t("Failed to get individual folders");
        showErrorMessage(errorMessage);
      } finally {
        setIsIndividualFoldersLoading(false);
      }
    },
    [showErrorMessage]
  );

  const getFields = React.useCallback(
    (params: { folderId?: number; searchQuery?: string; contextId?: number }) => {
      return getFolderFields({
        folderId: params.folderId,
        contextId:params.contextId??1,
        searchQuery: params.searchQuery,
        includeTables: true,
        includePDSFields: Boolean(params.searchQuery),
      });
    },
    [spreadsheetType]
  );

  const resetFoldersCache = React.useCallback(() => {
    foldersLoadedRef.current = false;
  }, []);

  const handleGetFolders = React.useCallback(async () => {
    // Only fetch folders if they haven't been loaded yet
    if (foldersLoadedRef.current) {
      return;
    }
    
    if (spreadsheetType === SpreadsheetType.individual) {
      handleGetIndividualFolders();
    }
    handleGetPedigreeFolders();
    foldersLoadedRef.current = true;
  }, [handleGetIndividualFolders, handleGetPedigreeFolders, spreadsheetType]);

  const handleGetFields = React.useCallback(
    async (params: {
      folderId?: FolderPrimaryListDto["id"];
      searchQuery?: string;
      contextId?: number;
    }) => {
      try {
        // Clear fields before making new request to prevent merging
        setFields([]);
        setIsFieldsLoading(true);
        // Set flag based on whether it's a search or folder selection
        setIsFieldsFromSearch(!!params.searchQuery);
        const fieldsData = await getFields(params);
        const filteredFieldsData = fieldsData.filter((field) => field.type !== 8)
        setFields(
          filteredFieldsData.map((field) => ({
            ...field,
            [itemUniqProperty]: generateId(),
            fullPath: field.fullPath || "",
          }))
        );
      } catch (err) {
        const errorMessage =
          (err as HttpErrorDto)?.entity || I18n.t("Failed to get fields");
        showErrorMessage(errorMessage);
      } finally {
        setIsFieldsLoading(false);
      }
    },
    [showErrorMessage, getFields]
  );

  const handleSelectedFolderChange = React.useCallback(
    (folder: FolderPrimaryListDto) => {
      setSelectedFolder(folder);
      // Load fields for both regular folders and system fields folder (ID 0)
      if (folder.id === 0) {
        // System fields folder - load system fields
        handleGetFields({ folderId: 0, contextId: folder.context as number });
      } else if (Boolean(Number(folder.id))) {
        // Regular folder - load folder fields
        handleGetFields({ folderId: folder.id, contextId: folder.context as number });
      }
    },
    [handleGetFields]
  );

  const findFolderById = React.useCallback(
    (list: FolderPrimaryListDto[], folderId: number): FolderPrimaryListDto | undefined => {
      for (const folder of list) {
        if (folder.id === folderId) {
          return folder;
        }
        if (folder.children && folder.children.length) {
          const childMatch = findFolderById(folder.children, folderId);
          if (childMatch) {
            return childMatch;
          }
        }
      }
      return undefined;
    },
    []
  );

  const handleSelectedFolderFieldChange = React.useCallback(
    (folderFields: InternalFieldPrimaryList[]) => {
      setSelectedFields(folderFields);
    },
    []
  );

  const handleFieldDoubleClick = React.useCallback(
    ({
      item: field,
    }: {
      item: InternalFieldPrimaryList;
      event: React.MouseEvent<HTMLDivElement>;
    }) => {
      if (!selectedFolder) {
        return;
      }

      const hasDuplicate = columnsTableFields.some((column) => {
        const sameIdAndSystem = column.field.id === field.id && column.field.system === field.system;
        
        if (!sameIdAndSystem) {
          return false;
        }
        
        const columnParent = column.parentField;
        const folderParent = field.parentField;

        if (columnParent === null && folderParent === null) {
          return true;
        }
        if ((columnParent === null && folderParent !== null) || (columnParent !== null && folderParent === null)) {
          return false;
        }
        if (columnParent !== null && columnParent !== undefined && folderParent !== null && folderParent !== undefined) {
          return columnParent.id === folderParent.id;
        }
        
        return false;
      });

      if (!hasDuplicate) {
        const newField: SpreadsheetManagementDraggedField = {
          id: generateId().toString(),
          field: field,
          folder: selectedFolder,
          parentField: field?.parentField,
        };

        setColumnsTableFields((prevFields) =>
          groupTableFieldsByParent([...prevFields, newField], {
            pinNonParentToTop: true,
          })
        );
      }
    },
    [selectedFolder, columnsTableFields, setColumnsTableFields]
  );

  const handleDropQueryBuilderConditions = React.useCallback(() => {
    const data: SkipLogicCondition[] = selectedFields.map((folderField) => ({
      id: generateId().toString(),
      field: folderField,
      leftParenthesis: false,
      rightParenthesis: false,
    }));

    setQueryBuilderConditions([...queryBuilderConditions, ...data]);
    setSelectedFields([]);
  }, [selectedFields, queryBuilderConditions]);

  const handleColumnsTableFieldDropAndClear = React.useCallback(() => {
    handleColumnsTableFieldDrop();
    setSelectedFields([]);
  }, [handleColumnsTableFieldDrop]);

  const handleQueryBuilderConditionsChange = React.useCallback(
    (conditions: SkipLogicCondition[]) => {
      setQueryBuilderConditions(conditions);
    },
    []
  );

  const handleAllQueryBuilderConditionsRemove = React.useCallback(() => {
    showConfirmMessage({
      cancelText: I18n.t("No"),
      confirmText: I18n.t("Yes"),
      confirmBtnId: "btn-message-box-ok",
      cancelBtnId: "btn-message-box-dont-cancel",
      content: I18n.t("Are you sure you wish to remove all query clauses?"),
      onCustomOk: () => {
        setQueryBuilderConditions([]);
      },
    });
  }, [showConfirmMessage]);

  const loadStoredFormatData = React.useCallback((storedData: { format: FormatInfo; columnModel: { smartColumns: SmartColumn[] }; queryModel?: { whereClauses: any[] } }) => {
    try {
      
      const { format, columnModel, queryModel } = storedData;

setSpreadsheetId(format.id);
      // Update current format
      setCurrentFormat({
        id: format.id,
        name: format.name,
        description: format.description,
        folderId: 0,
      });
      const filteredColumns = filterOutRowCountColumns(columnModel.smartColumns).filter((smartCol: any) => {
        const fid = smartCol.field.fieldid;
        const isDummy = fid === -500;
        return !isDummy;
      });
      // Populate columns table with loaded format columns
      const loadedColumns = filteredColumns.map((smartCol: any) => {
        let lookupValues = normalizeLookupValues(smartCol.field.lookupValues);

        

        return {
          id: generateId().toString(),
          field: {
            id: smartCol.field.fieldid,
            name: smartCol.field.fieldname||smartCol.field.name,
            system: smartCol.field.system,
            type: smartCol.field.type || smartCol.field.fieldtype?.typeid,
            context: spreadsheetType === SpreadsheetType.individual ? 1 : 2,
            imageURL: smartCol.parentField?.imageURL || smartCol.field.imageURL,
            fullPath: smartCol.field.fieldnameWithPath || smartCol.field.fieldname,
            editable: smartCol.editable || false,
            options: smartCol.field.options || smartCol.field.dropdownValues?.map((value: string) => ({
              actualValue: value,
              displayValue: value,
            })),
            lookupValues: lookupValues || null,
            parentField: smartCol.parentField || null,
            internalId: generateId(),
          },
          width: smartCol.width,
          folder: selectedFolder || {
            id: 0,
            name: "System",
            parentid: 0,
            secToken: 0,
            system: true,
            context: spreadsheetType === SpreadsheetType.individual ? 1 : 2,
            imageURL: "",
            imageURLSelected: "",
          },
          editable: smartCol.editable || false,
          lookupValues: lookupValues || null,
          parentField: smartCol.parentField,

        }
      });
      
      setColumnsTableFields(groupTableFieldsByParent(loadedColumns));
      
      // Populate sort table with loaded format sorting
      // Sort by sortOrder to preserve the order from the saved format
      const sortFields = filteredColumns
        .filter((smartCol: SmartColumn) => smartCol.sortOrder && smartCol.sortOrder > 0)
        .sort((a: SmartColumn, b: SmartColumn) => (a.sortOrder || 0) - (b.sortOrder || 0))
        .map((smartCol: SmartColumn) => ({
          id: generateId().toString(),
          field: {
            id: smartCol.field.fieldid,
            name: smartCol.field.fieldname,
            system: smartCol.field.system,
            type: smartCol.field.type,
            context: spreadsheetType === SpreadsheetType.individual ? 1 : 2,
            imageURL: smartCol.parentField?.imageURL || smartCol.field.imageURL,
            fullPath: smartCol.field.fieldnameWithPath || smartCol.field.fieldname,
            internalId: generateId(),
          },
          ...(smartCol?.parentField && { parentField: smartCol.parentField }),
          folder: selectedFolder || {
            id: 0,
            name: "System",
            parentid: 0,
            secToken: 0,
            system: true,
            context: spreadsheetType === SpreadsheetType.individual ? 1 : 2,
            imageURL: "",
            imageURLSelected: "",
          },
          direction: smartCol.sortAsc ? "ascending" as any : "descending" as any,
        }));
      
      setSortTableFields(sortFields as any);

      // Enrich queryModel with dropdown/options/lookup values from smartColumns before processing
      const enrichedQueryModel = hydrateQueryModelFromSmartColumns(queryModel, filteredColumns);

      // Populate query builder with loaded format conditions
      if (enrichedQueryModel?.whereClauses) {
        const queryConditions = enrichedQueryModel.whereClauses.map((clause: { field: { fieldid: number;imageURL: string; fieldname: string; name: string; system: boolean; type: number,options?: { actualValue: string; displayValue: string; }[];dropdownValues?: string[];fieldtype?: { typeid: number } }; operator: number; value: any; leftParenthesis?: boolean; rightParenthesis?: boolean; andOR?: string; andOr?: string; parentField?: { fieldid?: number; fieldId?: number; fieldname?: string; fieldName?: string; system?: boolean; type?: number,fieldtype?: { typeid: number }; imageURL?: string; } }) => {
          // Extract string value from clause.value object if it's an object
          let rawValue = "";
          if (clause.value) {
            if (typeof clause.value === "string") {
              rawValue = clause.value;
            } else if (clause.value.dataValue !== undefined) {
              rawValue = clause.value.dataValue;
            } else if (clause.value.rawValue !== undefined) {
              rawValue = clause.value.rawValue;
            } else if (clause.value.updateValue !== undefined) {
              rawValue = clause.value.updateValue;
            } else {
              // Fallback: convert to string if it's still an object
              rawValue = String(clause.value);
            }
          }

          // Convert raw value to display value using field information
          const fieldInfo = {
            fieldId: clause.field.fieldid,
            fieldName: clause.field.fieldname,
            isSystem: clause.field.system
          };
          const stringValue = convertFilterValueToDisplay(fieldInfo, rawValue);

          const condition: any = {
            id: generateId().toString(),
            field: {
              id: clause.field.fieldid,
              name: clause.field.fieldname|| clause.field.name,
              system: clause.field.system,
              type: clause.field.type || clause.field.fieldtype?.typeid,
              context: spreadsheetType === SpreadsheetType.individual ? 1 : 2,
              imageURL: clause.parentField?.imageURL || clause.field.imageURL,
              internalId: generateId(),
              fullPath: clause.field.fieldname,
              options:clause.field.options || clause.field.dropdownValues?.map((value: string) => ({
                actualValue: value,
                displayValue: value,
              })),
            },
            operator: getOperatorFromValue(clause.operator) as any,
            value: stringValue,
            leftParenthesis: clause.leftParenthesis || false,
            rightParenthesis: clause.rightParenthesis || false,
            andOR: clause.andOR || clause.andOr,
          };

          // Add parentField if it exists in the original clause
          if (clause.parentField) {

            // Match the smartColumn to get proper parentField
            const matchingSmartCol = filteredColumns.find(
                (col: SmartColumn) => col.field.fieldid === clause.field.fieldid
            );

            //Use parentField from smartColumn if available cause it has correct values
            const parentFieldFromCol = matchingSmartCol?.parentField;
            const pf: any = parentFieldFromCol || clause.parentField;

            const parentFieldId = parentFieldFromCol?.fieldid ||
                clause.parentField.fieldid ||
                clause.parentField.fieldId;

            if (parentFieldId && parentFieldId > 0) {
              condition.parentField = {
                id: parentFieldId,
                name: pf.fieldname || pf.fieldName || pf.name || '',
                system: pf.system || false,
                type: pf.fieldtype?.typeid || pf.type,
                context: spreadsheetType === SpreadsheetType.individual ? 1 : 2,
                imageURL: pf.imageURL || '',
                internalId: generateId(),
                fullPath: pf.fieldnameWithPath || pf.fieldname || pf.fieldName || pf.name || '',
              };
            }
          }
          return condition;
        });
        
        // Validate loaded conditions and show warning if invalid
        if (queryConditions && queryConditions.length > 0) {
          const validation = validateQueryConditions(queryConditions);
          if (!validation.isValid) {
            // Show warning but still load the conditions (user can fix them)
            showInfoMessage(validation.errorMessage || I18n.t("The loaded query has validation issues. Please review and fix before running."));
          }
        }
        
        setQueryBuilderConditions(queryConditions);
      }
      
      
    } catch (error) {
      showErrorMessage(I18n.t("Failed to load stored format data"));
    }
  }, [selectedFolder, setColumnsTableFields, setSortTableFields, setQueryBuilderConditions, showInfoMessage, showErrorMessage, filterOutRowCountColumns, spreadsheetType]);

  const loadSystemFieldsByDefault = React.useCallback(async () => {
    if (systemFieldsLoadedRef.current) {
      return; // Already loaded, prevent multiple calls
    }
    
    try {
      systemFieldsLoadedRef.current = true;
      setIsFieldsLoading(true); // Show loader
      
      // Load system fields with the specified API call parameters
      const systemFieldsData = await getFolderFields({
        folderId: 0, // System fields folder ID
        contextId: spreadsheetType === SpreadsheetType.individual ? FolderType.individual : FolderType.pedigreeSpreadsheet,
        searchQuery: "", // Empty query to get all system fields
        includeTables: true,
      });
      
      setFields(
        systemFieldsData.map((field) => ({
          ...field,
          [itemUniqProperty]: generateId(),
          fullPath: field.fullPath || "",
        }))
      );
      
      // Set system fields folder as selected
      const systemFolder: FolderPrimaryListDto = {
        id: 0,
        name: "System Fields",
        system: true,
        imageURL: "gif/system.gif",
        imageURLSelected: "gif/system.gif",
        parentid: 0,
        secToken: 0,
        context: spreadsheetType === SpreadsheetType.individual ? FolderContext.individual : FolderContext.pedigree,
      };
      setSelectedFolder(systemFolder);
      setIsFieldsFromSearch(false);
    } catch (err) {
      systemFieldsLoadedRef.current = false; // Reset on error so it can be retried
      const errorMessage =
        (err as HttpErrorDto)?.entity;
      showErrorMessage(errorMessage);
    } finally {
      setIsFieldsLoading(false); // Hide loader
    }
  }, [spreadsheetType]);

  // Helper to get format data from Redux
  const getFormatDataFromRedux = React.useCallback((spreadsheetId: number | string | undefined) => {
    if (spreadsheetId == null || spreadsheetId === undefined || typeof window === 'undefined' || !(window as any).__SPREADSHEET_STORE__) {
      return null;
    }
    try {
      const store = (window as any).__SPREADSHEET_STORE__;
      const state = store.getState();
      const spreadsheetTypeKey = spreadsheetType === SpreadsheetType.individual ? 'Individual' : 'Pedigree';
      const typeData = state?.spreadsheetFormat?.[spreadsheetTypeKey] || {};
      const storedData = typeData[Number(spreadsheetId)];
      if (storedData && storedData.columnModel && storedData.columnModel.smartColumns) {
        return {
          format: storedData.format || {
            id: Number(spreadsheetId),
            name: '',
            description: '',
            folderId: 0
          },
          columnModel: storedData.columnModel,
          queryModel: storedData.queryModel ? JSON.parse(JSON.stringify(storedData.queryModel)) : { whereClauses: [] }
        };
      }
    } catch (e) {
      console.warn('Could not retrieve format data from Redux:', e);
    }
    return null;
  }, [spreadsheetType]);

  // Helper to convert columnsTableFields to smartColumns format
  const convertColumnsToSmartColumns = React.useCallback((columnsFields: typeof columnsTableFields) => {
    const parentFieldIdToModulo = new Map<number, number>();
    let currentModulo = 0;

    const mappedColumns = columnsFields.map((columnField, index) => {
      const columnFieldAny = columnField as any;
      const sortFieldIndex = sortTableFields.findIndex(
        (sf) => sf.field.id === columnField.field.id
      );
      const sortField = sortTableFields[sortFieldIndex];
      const hasSortField = sortFieldIndex !== -1;
      const sortOrder = hasSortField ? sortFieldIndex + 1 : 0;
      const sortAsc =
        hasSortField && sortField?.direction
          ? sortField.direction === "ascending"
          : false;

      const fieldSourceRaw = (columnFieldAny.field || {}) as any;
      const fieldSource = {
        ...fieldSourceRaw,
        fieldid: fieldSourceRaw.fieldid ?? fieldSourceRaw.id,
        fieldname: fieldSourceRaw.fieldname ?? fieldSourceRaw.name,
        fieldcontext: 
          spreadsheetType === SpreadsheetType.individual
            ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL
            : SPREADSHEET_FIELD_CONTEXT.PEDIGREE
        ,
        type: fieldSourceRaw.type ?? fieldSourceRaw.fieldtype?.typeid,
        imageURL: fieldSourceRaw.imageURL ?? fieldSourceRaw.imageUrl,
        fullPath: fieldSourceRaw.fullPath ?? fieldSourceRaw.fieldnameWithPath,
        lookupValues: columnFieldAny.lookupValues ?? fieldSourceRaw.lookupValues ?? {},
        dropdownValues: fieldSourceRaw.dropdownValues ?? (
          Array.isArray(fieldSourceRaw.options)
            ? fieldSourceRaw.options.map((opt: any) =>
                typeof opt === "string" ? opt : opt.actualValue ?? opt.displayValue ?? opt
              )
            : undefined
        ),
        options: fieldSourceRaw.options,
        editable: columnFieldAny.editable ?? fieldSourceRaw.editable ?? false,
      } as any;
 
      const fieldType = fieldSource.type ?? fieldSource.fieldtype?.typeid;
      const fieldContext = fieldSource.fieldcontext;

      const field: any = {
        fieldid: fieldSource.fieldid,
        fieldname: fieldSource.fieldname,
        fieldcontext: fieldContext,
        system: fieldSource.system ?? false,
        type: fieldType,
        imageURL: fieldSource.imageURL,
        fieldnameWithPath: fieldSource.fullPath,
        lookupValues: fieldSource.lookupValues ?? {},
        dropdownValues: fieldSource.dropdownValues,
        options: fieldSource.options,
        editable: fieldSource.editable,
      };

      if (fieldSource.fieldtype || fieldType !== undefined) {
        field.fieldtype = {
          ...(fieldSource.fieldtype || {}),
          typeid: fieldSource.fieldtype?.typeid ?? fieldType,
        };
      }

      const column: any = {
        width:
          columnFieldAny.width ??
          fieldSource.defaultWidth ??
          columnFieldAny.field?.defaultWidth ??
          SPREADSHEET_DEFAULTS.WIDTH,
        sortOrder,
        sortAsc,
        columnOrder: index + 1,
        columnCount: columnFieldAny.columnCount ?? fieldSource.columnCount ?? 1,
        visible: columnFieldAny.visible ?? fieldSource.visible ?? true,
        editable: columnFieldAny.editable ?? fieldSource.editable ?? false,
        securityEditable: columnFieldAny.securityEditable ?? fieldSource.securityEditable,
        dropdownOptions: columnFieldAny.dropdownOptions,
        dataDropdownOptions: columnFieldAny.dataDropdownOptions,
        field,
      };

      let moduloValue = columnFieldAny.modulo;

      if (columnFieldAny.parentField) {
        const pfRaw: any = columnFieldAny.parentField;
        const parentFieldType =
          pfRaw.type ??
          pfRaw.fieldtype?.typeid 
        const parentField: any = {
          fieldid: pfRaw.fieldid ?? pfRaw.id,
          fieldname: pfRaw.fieldname ?? pfRaw.fieldName ?? pfRaw.name,
          fieldcontext: spreadsheetType === SpreadsheetType.individual ? "IDS_CONTEXT" : "PDS_CONTEXT",
          system: pfRaw.system ?? false,
          type: parentFieldType,
          imageURL: pfRaw.imageURL ?? pfRaw.imageUrl,
          fieldnameWithPath: pfRaw.fieldnameWithPath ?? pfRaw.fullPath,
        };

        if (pfRaw.fieldtype || parentFieldType !== undefined) {
          parentField.fieldtype = {
            ...(pfRaw.fieldtype || {}),
            typeid: pfRaw.fieldtype?.typeid ?? parentFieldType,
          };
        }

        if (pfRaw.lookupValues) {
          parentField.lookupValues = pfRaw.lookupValues;
        }
        if (pfRaw.dropdownValues) {
          parentField.dropdownValues = pfRaw.dropdownValues;
        }
        if (pfRaw.options) {
          parentField.options = pfRaw.options;
        }

        column.parentField = parentField;

        const parentId = column.parentField.fieldid;
        if (moduloValue === undefined && typeof parentId === "number") {
          if (parentFieldIdToModulo.has(parentId)) {
            moduloValue = parentFieldIdToModulo.get(parentId);
          } else {
            moduloValue = currentModulo;
            parentFieldIdToModulo.set(parentId, moduloValue);
            currentModulo = currentModulo === 0 ? 1 : 0;
          }
        }
      }

      if (moduloValue !== undefined) {
        column.modulo = moduloValue;
      }

      return column;
    });
    return ensureRowCountColumn(mappedColumns, spreadsheetType);
  }, [spreadsheetType, sortTableFields]);

  // Helper to convert queryBuilderConditions to queryModel format
  const convertQueryConditionsToQueryModel = React.useCallback(() => {
    // Helper function to get operator value (defined inline to avoid dependency issues)
    const getOperatorValueLocal = (operator: string): number => {
      const operatorMap: Record<string, number> = OPERATOR_MAPPING;
      return operatorMap[operator] || 1; // Default to EQUALS if not found
    };

    
    // Create a map of smartColumns by fieldid for lookupValues enrichment
    const smartColumnsMap = new Map<number, any>();
    columnsTableFields.forEach((colField) => {
      if (colField.field.id) {
        smartColumnsMap.set(colField.field.id, colField);
      }
    });

    // Transform whereClauses to match table.controller.tsx format
    const whereClauses = queryBuilderConditions.map((condition:any, index) => {
      const fieldType = condition.field.system === true ? SPREADSHEET_TABLE_CONSTANTS.FIELD_TYPES.SYSTEM : condition.field.type;
      
      // Convert options to dropdownValues if needed
      const dropdownValues = condition.field.options?.map((opt: any) => 
        typeof opt === 'string' ? opt : (opt.actualValue || opt.displayValue || opt)
      ) || [];

      // Try to get lookupValues from matching smartColumn
      let lookupValues = undefined;
      const matchingSmartCol = smartColumnsMap.get(condition.field.id);
      if (matchingSmartCol) {
        lookupValues = matchingSmartCol.lookupValues || matchingSmartCol.field.lookupValues;
      }

      // Build field object with all metadata preserved
      const field: any = {
        fieldid: condition.field.id,
        type: fieldType,
        fieldcontext:
          spreadsheetType === SpreadsheetType.individual
            ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL
            : SPREADSHEET_FIELD_CONTEXT.PEDIGREE,
        system: condition.field.system,
        // Preserve fieldname
        fieldname: condition.field.name,
        // Preserve fieldtype structure
        fieldtype: {
          typeid: fieldType,
          typename: condition.field.system ? 'System' : undefined,
        },
        // Preserve imageURL
        imageURL: condition.field.imageURL,
        // Preserve fieldnameWithPath (from fullPath)
        fieldnameWithPath: condition.field.fullPath,
        // Preserve dropdownValues
        dropdownValues: dropdownValues.length > 0 ? dropdownValues : undefined,
        // Preserve options if available
        options: condition.field.options || undefined,
        // Preserve lookupValues if available from matching smartColumn
        lookupValues: lookupValues || undefined,
      };

      const fieldInfo = {
        fieldId: condition.field.id,
        fieldName: condition.field.name,
        isSystem: condition.field.system
      };
      const actualValue = convertFilterValue(fieldInfo, condition.value || "");

      const clause: any = {
        field: field,
        operator: getOperatorValueLocal(condition.operator || "EQUALS"),
        value: actualValue,
        ...(condition.andOR && { andOr: condition.andOR }), // Note: andOr not andOR
        leftParenthesis: condition.leftParenthesis,
        rightParenthesis: condition.rightParenthesis,
        andOR: condition.andOR || condition.andOr as any,
      };

      // Conditionally add parentField to whereClauses if it exists
      if (condition.parentField) {
        const matchingSmartCol = smartColumnsMap.get(condition.field.id);
        const pf: any = matchingSmartCol?.parentField || condition.parentField;

        const parentFieldId = matchingSmartCol?.parentField?.fieldid ||
            matchingSmartCol?.parentField?.id ||
            condition.parentField.fieldid ||
            condition.parentField.id;

        if (parentFieldId && parentFieldId > 0) {
          const parentFieldType = pf.fieldtype?.typeid || pf.type || (pf.system ? SPREADSHEET_TABLE_CONSTANTS.FIELD_TYPES.SYSTEM : undefined);

          // Try to get lookupValues for parentField from matching smartColumn
          let parentLookupValues = undefined;
          if (parentFieldId) {
            const columnWithParentField = Array.from(smartColumnsMap.values()).find(
                (col: any) => col.parentField && (col.parentField.fieldid === parentFieldId || col.parentField.id === parentFieldId)
            );
            if (columnWithParentField?.parentField) {
              parentLookupValues = columnWithParentField.parentField.lookupValues;
            }
          }

          clause.parentField = {
            fieldid: parentFieldId,
            type: parentFieldType,
            fieldcontext:
                spreadsheetType === SpreadsheetType.individual
                    ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL
                    : SPREADSHEET_FIELD_CONTEXT.PEDIGREE,
            system: pf.system ?? false,
            // Preserve fieldname for parentField
            fieldname: pf.fieldname ?? pf.fieldName ?? pf.name,
            // Preserve fieldtype structure for parentField
            fieldtype: parentFieldType ? {
              typeid: parentFieldType,
              typename: pf.system ? 'System' : undefined,
            } : undefined,
            // Preserve imageURL for parentField if available
            imageURL: pf.imageURL,
            // Preserve fieldnameWithPath for parentField if available
            fieldnameWithPath: pf.fieldnameWithPath || pf.fieldname || pf.fieldName || pf.name,
            // Preserve lookupValues for parentField if available
            lookupValues: parentLookupValues || undefined,
          };
        }
      }
      return clause;
    });

    // Use computeQueryModel to normalize the query
    const queryModel = computeQueryModel(spreadsheetType, {
      userFilter: { whereClauses },
      normalizeForSave: true,
    });

    return queryModel;
  }, [spreadsheetType, queryBuilderConditions, columnsTableFields]);

  // Helper to update Redux with current columnModel only
  const updateReduxWithColumns = React.useCallback(() => {
    if (spreadsheetId == null || spreadsheetId === undefined || typeof window === 'undefined' || !(window as any).__SPREADSHEET_STORE__) {
      return;
    }
    try {
      const store = (window as any).__SPREADSHEET_STORE__;
      const { updateFormatData } = require('@modules/spreadsheets/actions/spreadsheetFormat');
      const spreadsheetTypeKey = spreadsheetType === SpreadsheetType.individual ? 'Individual' : 'Pedigree';

      const state = store.getState();
      // Build a map of preserved widths keyed by fieldid so we can reapply them

      const existingColumns: SmartColumn[] =
        state?.spreadsheetFormat?.[spreadsheetTypeKey]?.[Number(spreadsheetId)]?.columnModel?.smartColumns || [];
      const widthByFieldId = new Map<number, number>();
      existingColumns.forEach((col) => {
        const fieldId = col?.field?.fieldid;
        if (typeof fieldId === "number" && col?.width !== undefined) {
          widthByFieldId.set(fieldId, col.width);
        }
      });

      const smartColumns = convertColumnsToSmartColumns(columnsTableFields).map((col) => {
        const fieldId = col?.field?.fieldid;
        // Keep the stored width for existing fields; new fields fall back to default width

        if (typeof fieldId === "number" && widthByFieldId.has(fieldId)) {
          return {
            ...col,
            width: widthByFieldId.get(fieldId),
          };
        }
        return col;
      });

      store.dispatch(
        updateFormatData(spreadsheetId, spreadsheetTypeKey, {
          columnModel: { smartColumns },
        })
      );
    } catch (e) {
      console.warn('Could not update Redux with columns:', e);
    }
  }, [spreadsheetId, spreadsheetType, columnsTableFields, sortTableFields, convertColumnsToSmartColumns]);

  // Helper to update Redux with current queryModel only
  const updateReduxWithQuery = React.useCallback(() => {
    if (spreadsheetId == null || spreadsheetId === undefined || typeof window === 'undefined' || !(window as any).__SPREADSHEET_STORE__) {
      return;
    }
    try {
      const store = (window as any).__SPREADSHEET_STORE__;
      const { updateFormatData } = require('@modules/spreadsheets/actions/spreadsheetFormat');
      const spreadsheetTypeKey = spreadsheetType === SpreadsheetType.individual ? 'Individual' : 'Pedigree';
      
      // Convert queryBuilderConditions to queryModel
      const queryModel = convertQueryConditionsToQueryModel();
      
      // Deep clone queryModel to preserve all fields including andOr
      const clonedQueryModel = queryModel ? JSON.parse(JSON.stringify(queryModel)) : { whereClauses: [] };
      
      // Update Redux with new queryModel only (preserve existing columnModel)
      store.dispatch(updateFormatData(spreadsheetId, spreadsheetTypeKey, {
        queryModel: clonedQueryModel
      }));
    } catch (e) {
      console.warn('Could not update Redux with query:', e);
    }
  }, [spreadsheetId, spreadsheetType, queryBuilderConditions, columnsTableFields, convertQueryConditionsToQueryModel]);

  const initialize = React.useCallback(() => {
    handleGetFolders();
    isInitializingRef.current = true; // Set to true at start of initialization

    if (spreadsheetId != null && spreadsheetId !== undefined) {
        // Check Redux first - if data exists, use it directly
        const reduxFormatData = getFormatDataFromRedux(Number(spreadsheetId));
        
        if (reduxFormatData && reduxFormatData.columnModel) {
          // Use data from Redux - preserve exact structure including andOr fields
          loadStoredFormatData(reduxFormatData);
          // Mark initialization as complete after a short delay to allow loadStoredFormatData to complete
          setTimeout(() => {
            isInitializingRef.current = false;
          }, 100);
        } else if (spreadsheetId === 0) {
          // For unsaved spreadsheets (ID 0), initialize Redux with empty data
          if (typeof window !== 'undefined' && (window as any).__SPREADSHEET_STORE__) {
            try {
              const store = (window as any).__SPREADSHEET_STORE__;
              const { storeFormatData } = require('@modules/spreadsheets/actions/spreadsheetFormat');
              const spreadsheetTypeKey = spreadsheetType === SpreadsheetType.individual ? 'Individual' : 'Pedigree';
              
              const formatDataToStore = {
                format: {
                  id: 0,
                  name: '',
                  description: '',
                  folderId: 0
                },
                columnModel: { smartColumns: [] },
                queryModel: { whereClauses: [] },
                timestamp: Date.now()
              };
              
              store.dispatch(storeFormatData(0, spreadsheetTypeKey, formatDataToStore));
              
              // Set currentFormat to ID 0 for unsaved spreadsheets
              setCurrentFormat({
                id: 0,
                name: '',
                description: '',
                folderId: 0
              });
            } catch (e) {
              console.warn('Could not initialize Redux for unsaved spreadsheet:', e);
            }
          }
          isInitializingRef.current = false;
        } else if (columnsTableFields.length === 0) {
          // Data not in Redux - call API as fallback
          const loadFormatApi = spreadsheetType === SpreadsheetType.individual 
            ? loadIndividualSpreadsheetFormat 
            : loadPedigreeSpreadsheetFormat;

          (async () => {
            try {
              const formatData = await loadFormatApi(Number(spreadsheetId));
              if (formatData) {
                const columnModel = formatData.columnModel || formatData;
                const queryModel = formatData.queryModel ? JSON.parse(JSON.stringify(formatData.queryModel)) : { whereClauses: [] };
                const format: FormatInfo = {
                  id: Number(spreadsheetId),
                  name: (formatData as any)?.formatDetails?.name || '',
                  description: (formatData as any)?.formatDetails?.description || '',
                  folderId: (formatData as any)?.formatDetails?.folderId || 0,
                } as any;
                
                // Store in Redux after loading from API
                if (typeof window !== 'undefined' && (window as any).__SPREADSHEET_STORE__) {
                  try {
                    const store = (window as any).__SPREADSHEET_STORE__;
                    const { storeFormatData } = require('@modules/spreadsheets/actions/spreadsheetFormat');
                    const spreadsheetTypeKey = spreadsheetType === SpreadsheetType.individual ? 'Individual' : 'Pedigree';
                    
                    const formatDataToStore = {
                      format: {
                        id: Number(spreadsheetId),
                        name: format.name,
                        description: format.description || '',
                        folderId: format.folderId || 0
                      },
                      columnModel: columnModel,
                      queryModel: queryModel,
                      timestamp: Date.now()
                    };
                    
                    store.dispatch(storeFormatData(Number(spreadsheetId), spreadsheetTypeKey, formatDataToStore));
                  } catch (e) {
                    console.warn('Could not store format data in Redux:', e);
                  }
                }
                
                loadStoredFormatData({ format, columnModel, queryModel });
              }
            } catch (e) {
              // ignore; user can load manually
            } finally {
              // Mark initialization as complete after a short delay to allow loadStoredFormatData to complete
              setTimeout(() => {
                isInitializingRef.current = false;
              }, 100);
            }
          })();
        } else {
          // Already have columns loaded, mark initialization as complete
          isInitializingRef.current = false;
        }
    } else {
      isInitializingRef.current = false;
    }
  }, [filterOutRowCountColumns, spreadsheetType, loadStoredFormatData, handleGetFolders, getFormatDataFromRedux, spreadsheetId]);

  React.useEffect(() => {
    initialize();
  }, [initialize]);

  React.useEffect(() => {
    if (initialFolderAppliedRef.current) {
      return;
    }
    if (!initialFolderId || initialFolderId <= 0) {
      return;
    }
    const availableFolders =
      spreadsheetType === SpreadsheetType.individual
        ? individualFolders
        : pedigreeFolders;
    if (!availableFolders.length) {
      return;
    }
    const matchedFolder = findFolderById(availableFolders, initialFolderId);
    if (matchedFolder) {
      handleSelectedFolderChange(matchedFolder);
      initialFolderAppliedRef.current = true;
    }
  }, [
    initialFolderId,
    spreadsheetType,
    individualFolders,
    pedigreeFolders,
    handleSelectedFolderChange,
    findFolderById,
  ]);

  // Update Redux whenever queryBuilderConditions changes
  React.useEffect(() => {
    // Only update if we have a valid spreadsheetId (including 0 for unsaved) and we're not initializing
    if (spreadsheetId != null && spreadsheetId !== undefined && !isInitializingRef.current) {
      updateReduxWithQuery();
    }
  }, [queryBuilderConditions, spreadsheetId, updateReduxWithQuery]);
    
  // Reset folders cache when spreadsheet type changes
  React.useEffect(() => {
    resetFoldersCache();
  }, [spreadsheetType, resetFoldersCache]);

  // Load system fields by default when component mounts
  React.useEffect(() => {
    loadSystemFieldsByDefault();
  }, [loadSystemFieldsByDefault]);

  // Update Redux whenever columnsTableFields changes (for both edit scenarios)
  React.useEffect(() => {
    // Only update if we have a valid spreadsheetId (including 0 for unsaved) and we're not initializing
    if (spreadsheetId != null && spreadsheetId !== undefined && !isInitializingRef.current) {
      updateReduxWithColumns();
    }
  }, [columnsTableFields, spreadsheetId, updateReduxWithColumns]);

  const getId = React.useMemo(
    () => ({
      builderFieldsPan: {
        bodyRow: ({ rowIndex }: { rowIndex: number }) =>
          `${currentSpreadsheetTypeConfig.idPrefix}-builder-fields-pan-data-tbody-row${rowIndex}`,
        bodyRowCell: ({
          rowIndex,
          cellIndex,
        }: {
          rowIndex: number;
          cellIndex: number;
        }) =>
          `${currentSpreadsheetTypeConfig.idPrefix}-builder-fields-pan-data-tbody-row${rowIndex}-cell${cellIndex}`,
        headerRow: `${currentSpreadsheetTypeConfig.idPrefix}-builder-fields-pan-data-thead-row0`,
        headerRowCell: ({ cellIndex }: { cellIndex: number }) =>
          `-builder-fields-pan-data-thead-row0-cell${cellIndex + 1}`,
      },
      builderSortPan: {
        bodyRow: ({ rowIndex }: { rowIndex: number }) =>
          `${currentSpreadsheetTypeConfig.idPrefix}-builder-sort-pan-data-tbody-row${rowIndex}`,
        bodyRowCell: ({
          rowIndex,
          cellIndex,
        }: {
          rowIndex: number;
          cellIndex: number;
        }) =>
          `${currentSpreadsheetTypeConfig.idPrefix}-builder-sort-pan-data-tbody-row${rowIndex}-cell${cellIndex}`,
        headerRow: `${currentSpreadsheetTypeConfig.idPrefix}-builder-fields-pan-data-thead-row0`,
        headerRowCell: ({ cellIndex }: { cellIndex: number }) =>
          `${
            currentSpreadsheetTypeConfig.idPrefix
          }-builder-sort-pan-data-tbody-row0-cell${cellIndex + 1}`,
      },
      fields: {
        rowId: ({ rowIndex }: { rowIndex: number }) =>
          `${currentSpreadsheetTypeConfig.idPrefix}-builder-whs-fild-list-grid-${rowIndex}-0`,
      },
      folders: {
        folder: ({ parentIndex, ownIndex }: TreeGetIdArgs) =>
          parentIndex === undefined
            ? `${currentSpreadsheetTypeConfig.idPrefix}-builder-whs-treewnd-tree-tree-root-child${ownIndex}-content`
            : `${currentSpreadsheetTypeConfig.idPrefix}-builder-whs-treewnd-tree-tree-root-child${parentIndex}-child${ownIndex}-content`,
      },
    }),
    [currentSpreadsheetTypeConfig]
  );

  const folders = React.useMemo(
    () =>
      [...individualFolders, ...pedigreeFolders].sort(
        (a, b) => Number(b.system) - Number(a.system)
      ),
    [individualFolders, pedigreeFolders]
  );

  return {
    folders,
    isPedigreeFoldersLoading,
    isIndividualFoldersLoading,
    fields,
    isFieldsFromSearch,
    handleSelectedFolderChange,
    handleGetFields,
    selectedFolder,
    isFieldsLoading,
    selectedFields,
    handleSelectedFolderFieldChange,
    handleFieldDoubleClick,
    handleDropQueryBuilderConditions,
    handleQueryBuilderConditionsChange,
    handleAllQueryBuilderConditionsRemove,
    queryBuilderConditions,
    messageProps,
    closeMessage,
    draggedUniqProperty,
    itemUniqProperty,
    currentSpreadsheetTypeConfig,
    getId,
    handleColumnsTableFieldsRemove,
    handleSortTableFieldsRemove,
    setColumnsTableFields,
    handleColumnsTableFieldDrop: handleColumnsTableFieldDropAndClear,
    columnsTable,
    columnsTableFields,
    columnsSortTable,
    sortTableFields,
    setSortTableFields,
    handleLoadFormat,
    handleSaveFormat,
    formatManagementController,
    isAllowedSaveFormatSubmit,
    currentFormat,
    showResults,
    runPayload,
    handleRunSpreadsheet,
    setShowResults,
    isRunSpreadsheetLoading,
    spreadsheetId,
    handleRowDrop
  };
};
